import { Badge, type BadgeProps, Button, Field, Input } from "@norican/react";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { type ChartMode, OrgChart } from "@/components/org-chart";
import { PresentationDialog } from "@/components/org-slide";
import { Notice, Page } from "@/components/page";
import { PersonSearch } from "@/components/person-search";
import { control, subText, table, td, th } from "@/components/table";
import { AdView } from "@/features/org-ad";
import { AdhocPanel, UndoButton } from "@/features/org-adhoc";
import {
  type ImportReport,
  type ImportRow,
  type OrgChange,
  type OrgPerson,
  type OrgPick,
  type ScenarioDetail,
  type ScenarioSummary,
  type SimIssue,
  useCreateBlankOrgScenario,
  useCreateOrgScenario,
  useDeleteOrgChange,
  useMe,
  useOrgScenario,
  useOrgScenarioAction,
  useOrgScenarios,
  useOrgSnapshot,
  usePreviewOrgImport,
  usePutOrgChange,
  useRefreshOrgSnapshot,
  useRenameOrgScenario,
} from "@/lib/api";
import { dateInZone, errorMessage } from "@/lib/format";
import {
  commonAncestor,
  FIELD_LABEL,
  indexChanges,
  type OrgMap,
  pathToTop,
  project,
  toMap,
  topOf,
} from "@/lib/org";
import { personName } from "@/lib/org-slide";

/**
 * Org chart — the directory's reporting structure, and what a target
 * structure from HR would change in it.
 *
 * Three things on one page: the current org drawn from a frozen snapshot of
 * the directory; an import of HR's workbook (their own name-keyed layout, or
 * the HROps template) that resolves every name to an account and asks where it
 * cannot; and a scenario view that draws before and after and lists what is
 * wrong with the result. Nothing here changes the directory. The export is a
 * change list and a reviewable PowerShell script for whoever changes AD.
 */

/**
 * The three jobs this page does, as three views rather than one long scroll:
 * look at the directory, work on a target structure, or change one account.
 * They were stacked until 2 Oct 2026 and the page had grown past the point
 * where anyone could find anything on it.
 *
 * "ad" (4 Nov 2026) is the read-only table of everyone's work fields, in
 * `org-ad.tsx`. It reads every member row rather than the chart's set, so the
 * chart's own snapshot is not fetched while it is open.
 */
type View = "directory" | "ad" | "scenarios" | "adhoc";
const VIEWS: readonly View[] = ["directory", "ad", "scenarios", "adhoc"];
const isView = (v: string | null): v is View => VIEWS.includes(v as View);

export function OrgPage() {
  const me = useMe().data;
  const enabled = me?.org_chart_enabled === true;
  const [showArchived, setShowArchived] = useState(false);
  const scenarios = useOrgScenarios(enabled, showArchived);
  const refresh = useRefreshOrgSnapshot();
  const [selected, setSelected] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  // The view is also in the URL (`?view=`), so the nav's submenu and a
  // bookmark can open a tab directly, and the back button returns to it.
  const [params, setParams] = useSearchParams();
  const paramView = params.get("view");
  const [view, setViewState] = useState<View>(isView(paramView) ? paramView : "directory");
  const snapshot = useOrgSnapshot(enabled && view !== "ad");
  useEffect(() => {
    if (isView(paramView) && paramView !== view) {
      setViewState(paramView);
    }
    // Only when the URL changes under us (nav click, back button).
  }, [paramView]);
  const setView = useCallback(
    (next: View) => {
      setViewState(next);
      setParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          n.set("view", next);
          return n;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  if (me && !enabled) {
    return (
      <Page title="Org chart">
        <Notice>This page is not available to your account.</Notice>
      </Page>
    );
  }

  const snap = snapshot.data?.snapshot;
  const mayAdhoc = snapshot.data?.adhoc?.available === true;
  // The tab exists only while the deployment keeps the ad-hoc door open. Since
  // 6 Oct 2026 it is closed: a change of supervisor, title, department, area,
  // country, city or legal entity is reported on the Workforce page and written
  // to AD on approval, through the very same path.
  const adhocTab = me?.adhoc_enabled === true;
  const allViews: [View, string, string][] = [
    ["directory", "Directory", "Reporting lines as the directory has them right now."],
    [
      "ad",
      "Active Directory",
      "Every person's work fields as Entra ID has them, read every 15 minutes. Read-only: a wrong value is corrected through Workforce, Report a change.",
    ],
    [
      "scenarios",
      `Scenarios${scenarios.data?.length ? ` (${scenarios.data.length})` : ""}`,
      "A target structure for many people: prepare, approve, test against AD, apply.",
    ],
    [
      "adhoc",
      "Change an account",
      "One person's title, manager, department or company, written to AD on confirmation.",
    ],
  ];
  const views = allViews.filter(([v]) => v !== "adhoc" || adhocTab);
  const shownView: View = view === "adhoc" && !adhocTab ? "directory" : view;
  const intro = views.find(([v]) => v === shownView)?.[2] ?? "";

  // Opening a scenario or an import is scenario work; never leave the person
  // on a tab whose content has just moved somewhere else.
  const openScenario = (id: string) => {
    setImporting(false);
    setSelected(id);
    setView("scenarios");
  };

  return (
    <Page
      title="Org chart"
      intro={intro}
      actions={
        // The Active Directory view carries its own freshness line, Refresh
        // and Export, next to the list they act on.
        shownView === "ad" ? null : (
          <div className="flex items-center gap-3">
            <span className={subText}>
              {snap
                ? `Directory as of ${dateInZone(snap.checked_at)} ${new Date(snap.checked_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}, ${snap.person_count} accounts`
                : "No directory snapshot yet"}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={refresh.isPending}
              onClick={() => refresh.mutate()}
            >
              {refresh.isPending ? "Reading the directory…" : "Refresh directory"}
            </Button>
          </div>
        )
      }
    >
      {refresh.error ? (
        <Notice tone="danger">
          {errorMessage(refresh.error, "Could not read the directory.")}
        </Notice>
      ) : null}

      <div className="border-border mt-4 flex gap-4 border-b" role="tablist">
        {views.map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={shownView === key}
            onClick={() => setView(key)}
            className={
              shownView === key
                ? "text-fg border-fg -mb-px border-b-2 pb-2 text-sm font-medium"
                : "text-fg-muted hover:text-fg pb-2 text-sm"
            }
          >
            {label}
          </button>
        ))}
      </div>

      {shownView === "ad" ? (
        <AdView enabled={enabled} mayReport={me?.workforce_enabled === true} />
      ) : snapshot.isLoading ? (
        <Notice>Loading the directory…</Notice>
      ) : snapshot.error ? (
        <Notice tone="danger">{errorMessage(snapshot.error)}</Notice>
      ) : !snap ? (
        <Notice>Take a snapshot of the directory first: Refresh directory, top right.</Notice>
      ) : shownView === "directory" ? (
        <DirectoryView people={snapshot.data?.people ?? []} initialRoot={params.get("root")} />
      ) : shownView === "adhoc" ? (
        <AdhocPanel people={snapshot.data?.people ?? []} enabled={mayAdhoc} />
      ) : (
        <>
          <section className="mt-6">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="text-fg text-sm font-semibold">Scenarios</h2>
              <div className="flex items-center gap-3">
                <label className="text-fg-muted flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                  />
                  Show archived
                </label>
                <NewScenario disabled={!snap} onCreated={openScenario} />
                <Button size="sm" disabled={!snap} onClick={() => setImporting(true)}>
                  Import workbook
                </Button>
              </div>
            </div>
            <ScenarioList
              scenarios={scenarios.data ?? []}
              selected={selected}
              onSelect={(id) => {
                setSelected(id === selected ? null : id);
                setImporting(false);
              }}
            />
          </section>
          {importing ? (
            <ImportPanel onCancel={() => setImporting(false)} onCreated={openScenario} />
          ) : selected ? (
            <ScenarioView id={selected} onGone={() => setSelected(null)} onOpen={openScenario} />
          ) : null}
        </>
      )}
    </Page>
  );
}

/** An empty scenario, to be built by hand from the chart or the Changes tab. */
function NewScenario({
  disabled,
  onCreated,
}: {
  disabled: boolean;
  onCreated: (id: string) => void;
}) {
  const create = useCreateBlankOrgScenario();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  if (!open) {
    return (
      <Button size="sm" variant="outline" disabled={disabled} onClick={() => setOpen(true)}>
        New scenario
      </Button>
    );
  }
  return (
    <form
      className="flex items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) {
          create.mutate(name.trim(), {
            onSuccess: (s) => {
              setOpen(false);
              setName("");
              if (s) {
                onCreated(s.id);
              }
            },
          });
        }
      }}
    >
      <Input
        placeholder="Scenario name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
      />
      <Button size="sm" type="submit" disabled={!name.trim() || create.isPending}>
        Create
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
        Cancel
      </Button>
      {create.error ? (
        <span className="text-danger-fg text-xs">{errorMessage(create.error)}</span>
      ) : null}
    </form>
  );
}

// ─── scenario list ───────────────────────────────────────────────────────

const STATUS: Record<string, { label: string; tone: BadgeProps["tone"] }> = {
  draft: { label: "draft", tone: "neutral" },
  approved: { label: "approved", tone: "info" },
  testing: { label: "testing against AD", tone: "info" },
  applying: { label: "applying", tone: "warning" },
  applied: { label: "applied", tone: "success" },
  partial: { label: "partly applied", tone: "danger" },
  archived: { label: "archived", tone: "neutral" },
};

function StatusBadge({ status }: { status: string }) {
  const st = STATUS[status] ?? { label: status, tone: "neutral" as const };
  return (
    <Badge tone={st.tone} variant="subtle">
      {st.label}
    </Badge>
  );
}

function ScenarioList({
  scenarios,
  selected,
  onSelect,
}: {
  scenarios: ScenarioSummary[];
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  if (scenarios.length === 0) {
    return <p className={`${subText} mt-2`}>None yet. Import HR's workbook to create one.</p>;
  }
  return (
    <table className={`${table} mt-2`}>
      <thead>
        <tr>
          <th className={th}>Name</th>
          <th className={th}>Status</th>
          <th className={th}>From file</th>
          <th className={th}>Changes</th>
          <th className={th}>Created</th>
        </tr>
      </thead>
      <tbody>
        {scenarios.map((s) => (
          <tr
            key={s.id}
            onClick={() => onSelect(s.id)}
            className={`cursor-pointer ${selected === s.id ? "bg-bg-muted" : "hover:bg-bg-subtle"}`}
          >
            <td className={`${td} font-medium`}>
              <RowName id={s.id} name={s.name} />
            </td>
            <td className={td}>
              <StatusBadge status={s.status} />
            </td>
            <td className={td}>{s.source_filename ?? "—"}</td>
            <td className={td}>
              {s.change_count ?? 0}
              {s.written_count ? (
                <div className={subText}>
                  {s.written_count} in AD, {s.verified_count ?? 0} in Entra
                </div>
              ) : null}
            </td>
            <td className={td}>
              {dateInZone(s.created_at)}
              <div className={subText}>{s.created_by_upn}</div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * The scenario name in the list, renameable in place.
 *
 * Renaming has always been possible — it is a `PATCH` any org-chart user may
 * call, in every state, because a name changes no directory value. It was
 * only reachable from the opened scenario's header, which is not where anyone
 * looks for it. `stopPropagation` keeps a click on the pencil from also
 * selecting the row.
 */
function RowName({ id, name }: { id: string; name: string }) {
  const rename = useRenameOrgScenario();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  if (!editing) {
    return (
      <span className="group flex items-center gap-2">
        {name}
        <button
          className="text-fg-muted hover:text-fg text-xs underline opacity-0 group-hover:opacity-100 focus:opacity-100"
          aria-label={`Rename ${name}`}
          onClick={(e) => {
            e.stopPropagation();
            setValue(name);
            setEditing(true);
          }}
        >
          rename
        </button>
      </span>
    );
  }
  return (
    <form
      className="flex items-center gap-2"
      onClick={(e) => e.stopPropagation()}
      onSubmit={(e) => {
        e.preventDefault();
        if (value.trim()) {
          rename.mutate({ id, name: value.trim() }, { onSuccess: () => setEditing(false) });
        }
      }}
    >
      <Input value={value} onChange={(e) => setValue(e.target.value)} autoFocus />
      <Button size="sm" type="submit" disabled={rename.isPending}>
        Save
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
        Cancel
      </Button>
    </form>
  );
}

// ─── import ──────────────────────────────────────────────────────────────

const VERDICT: Record<string, { label: string; tone: BadgeProps["tone"] }> = {
  change: { label: "change", tone: "info" },
  no_change: { label: "no change", tone: "neutral" },
  needs_pick: { label: "needs a pick", tone: "warning" },
  duplicate: { label: "duplicate", tone: "neutral" },
};

const MATCH_NOTE: Record<string, string> = {
  swapped: "matched with the name parts swapped",
  partial: "matched on surname and first given name",
  fuzzy: "close spelling only",
  ambiguous: "several accounts carry this name",
  disabled: "only a disabled account",
  none: "no account found",
  vacancy: "open position",
  picked: "picked by you",
  skipped: "left as it is",
};

function ImportPanel({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (id: string) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [picks, setPicks] = useState<Record<string, OrgPick>>({});
  const [name, setName] = useState("");
  const [filter, setFilter] = useState<"all" | "attention">("attention");
  const preview = usePreviewOrgImport();
  const create = useCreateOrgScenario();
  const report = preview.data;

  const run = (f: File, p: Record<string, OrgPick>) => preview.mutate({ file: f, picks: p });
  const pick = (key: string, patch: OrgPick) => {
    const next = { ...picks, [key]: { ...picks[key], ...patch } };
    setPicks(next);
    if (file) {
      run(file, next);
    }
  };

  const rows = (report?.rows ?? []).filter(
    (r) => filter === "all" || r.verdict !== "change" || r.notes.length > 0,
  );

  return (
    <section className="border-border mt-6 border-t pt-4">
      <h2 className="text-fg text-sm font-semibold">Import a target structure</h2>
      <p className={`${subText} mt-1 max-w-prose`}>
        HR's layout (Name, Current Manager, Future Manager, Future job title, Future department) or
        the HROps template. A blank cell means no change, never "clear it". Names are matched to
        accounts; where the match is not certain you pick the account. Nothing is saved until you
        create the scenario.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <input
          type="file"
          accept=".xlsx,.xlsm"
          className={control}
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            setFile(f);
            setPicks({});
            if (f) {
              setName(f.name.replace(/\.xlsx?m?$/i, ""));
              run(f, {});
            }
          }}
        />
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {preview.error ? (
        <Notice tone="danger">{errorMessage(preview.error, "Could not read the workbook.")}</Notice>
      ) : null}
      {preview.isPending && !report ? <Notice>Reading the workbook…</Notice> : null}

      {report ? (
        <>
          <ImportSummary report={report} />
          <div className="mt-3 flex items-center gap-3 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={filter === "attention"}
                onChange={() => setFilter("attention")}
              />
              Rows that need a look
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" checked={filter === "all"} onChange={() => setFilter("all")} />
              All {report.row_count} rows
            </label>
          </div>
          <ImportTable rows={rows} picks={picks} onPick={pick} />
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <Field label="Scenario name" className="min-w-80">
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Button
              disabled={!file || !name.trim() || create.isPending || preview.isPending}
              onClick={() =>
                file &&
                create.mutate(
                  { file, name: name.trim(), picks },
                  { onSuccess: (s) => s && onCreated(s.id) },
                )
              }
            >
              {create.isPending
                ? "Creating…"
                : `Create scenario with ${report.counts.change ?? 0} changed people`}
            </Button>
          </div>
          {(report.counts.needs_pick ?? 0) > 0 ? (
            <p className={`${subText} mt-2`}>
              {report.counts.needs_pick} row(s) still need a pick and will be left out.
            </p>
          ) : null}
          {create.error ? (
            <Notice tone="danger">{errorMessage(create.error, "Could not create it.")}</Notice>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function ImportSummary({ report }: { report: ImportReport }) {
  const withNotes = report.rows.filter((r) => r.notes.length > 0).length;
  return (
    <p className="text-fg mt-3 text-sm">
      {report.filename}: {report.row_count} rows on {report.sheets.join(", ")} (
      {report.layout === "hr-names" ? "HR layout, matched by name" : "HROps template, by UPN"}).{" "}
      {Object.entries(report.counts)
        .map(([k, v]) => `${v} ${VERDICT[k]?.label ?? k}`)
        .join(", ")}
      . {withNotes} with a note.
      {report.ignored_sheets?.length ? ` Ignored sheets: ${report.ignored_sheets.join(", ")}.` : ""}
    </p>
  );
}

function ImportTable({
  rows,
  picks,
  onPick,
}: {
  rows: ImportRow[];
  picks: Record<string, OrgPick>;
  onPick: (key: string, patch: OrgPick) => void;
}) {
  if (rows.length === 0) {
    return <p className={`${subText} mt-3`}>Nothing needs a look.</p>;
  }
  return (
    <div className="mt-2 overflow-x-auto">
      <table className={table}>
        <thead>
          <tr>
            <th className={th}>Row</th>
            <th className={th}>Person</th>
            <th className={th}>Future manager</th>
            <th className={th}>Changes</th>
            <th className={th}>Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td className={td}>
                {r.row}
                <div className="mt-1">
                  <Badge tone={VERDICT[r.verdict]?.tone} variant="subtle">
                    {VERDICT[r.verdict]?.label ?? r.verdict}
                  </Badge>
                </div>
              </td>
              <td className={td}>
                <div className={subText}>{String(r.values.name ?? r.values.upn ?? "")}</div>
                <Match
                  match={r.person}
                  value={picks[r.key]?.person_id}
                  onPick={(id) => onPick(r.key, { person_id: id })}
                />
              </td>
              <td className={td}>
                <div className={subText}>{String(r.values.future_manager ?? "")}</div>
                <Match
                  match={r.manager}
                  value={picks[r.key]?.manager_id}
                  allowSkip
                  onPick={(id) => onPick(r.key, { manager_id: id })}
                />
              </td>
              <td className={td}>
                {r.changes.map((c) => (
                  <div key={c.field}>
                    <span className={subText}>{FIELD_LABEL[c.field] ?? c.field}: </span>
                    <span className="line-through opacity-60">{c.old_label ?? "—"}</span> →{" "}
                    <span className="font-medium">{c.new_label ?? "—"}</span>
                  </div>
                ))}
              </td>
              <td className={`${td} text-warning-fg max-w-80`}>
                {r.notes.map((n) => (
                  <div key={n}>{n}</div>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Match({
  match,
  value,
  allowSkip = false,
  onPick,
}: {
  match: ImportRow["person"];
  value: string | undefined;
  allowSkip?: boolean;
  onPick: (id: string) => void;
}) {
  const confident = ["exact", "swapped", "partial", "picked", "blank"].includes(match.status);
  if (confident && match.resolved) {
    return (
      <div>
        {match.resolved.display_name}
        <div className={subText}>
          {match.resolved.upn}
          {MATCH_NOTE[match.status] ? ` · ${MATCH_NOTE[match.status]}` : ""}
        </div>
      </div>
    );
  }
  if (match.status === "blank") {
    return <span className={subText}>no change</span>;
  }
  const cands = match.candidates ?? [];
  return (
    <div>
      <div className="text-warning-fg text-xs">{MATCH_NOTE[match.status] ?? match.status}</div>
      {cands.length > 0 || allowSkip ? (
        <select
          className={`${control} mt-1 max-w-72`}
          value={value ?? "-"}
          onChange={(e) => e.target.value !== "-" && onPick(e.target.value)}
        >
          <option value="-">Pick…</option>
          {cands.map((c) => (
            <option key={c.id} value={c.id}>
              {c.display_name} · {c.upn}
              {c.account_enabled ? "" : " (disabled)"}
            </option>
          ))}
          {allowSkip ? <option value="">Leave the manager as it is</option> : null}
        </select>
      ) : null}
    </div>
  );
}

// ─── scenario ────────────────────────────────────────────────────────────

type Tab = "chart" | "changes" | "issues" | "managers" | "left-out";

const SEVERITY_TONE: Record<string, BadgeProps["tone"]> = {
  error: "danger",
  warning: "warning",
  info: "info",
};

function ScenarioView({
  id,
  onGone,
  onOpen,
}: {
  id: string;
  onGone: () => void;
  //: Where to go when an undo prepared here produces a new scenario.
  onOpen?: (id: string) => void;
}) {
  const { data, isLoading, error } = useOrgScenario(id);
  const [tab, setTab] = useState<Tab>("chart");
  if (isLoading) {
    return <Notice>Simulating…</Notice>;
  }
  if (error || !data) {
    return <Notice tone="danger">{errorMessage(error)}</Notice>;
  }

  const sim = data.simulation;
  const byField = sim.changes_by_field;
  const leftOut = (data.import_report?.rows ?? []).filter(
    (r) => r.verdict === "needs_pick" || r.verdict === "duplicate",
  );
  const tabs: [Tab, string][] = [
    ["chart", "Chart"],
    ["changes", `Changes (${data.changes.length})`],
    ["issues", `Findings (${sim.issues.length})`],
    ["managers", `Managers (${sim.managers.length})`],
    ["left-out", `Left out (${leftOut.length})`],
  ];
  return (
    <section className="border-border mt-6 border-t pt-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <ScenarioTitle id={id} name={data.scenario.name} status={data.scenario.status} />
        <div className="flex items-center gap-2">
          <UndoButton
            scenarioId={id}
            status={data.scenario.status}
            writtenCount={data.scenario.written_count ?? 0}
            onPrepared={onOpen}
          />
          <a
            className={`${control} hover:bg-bg-subtle`}
            href={`/api/org/scenarios/${id}/export.xlsx`}
          >
            Change list (.xlsx)
          </a>
          <a
            className={`${control} hover:bg-bg-subtle`}
            href={`/api/org/scenarios/${id}/export.ps1`}
          >
            AD script (.ps1)
          </a>
        </div>
      </div>
      <p className="text-fg mt-1 text-sm">
        {sim.changed_people} people change: {byField.manager ?? 0} manager lines,{" "}
        {byField.job_title ?? 0} titles, {byField.department ?? 0} departments
        {byField.company_name ? `, ${byField.company_name} companies` : ""}.{" "}
        {sim.issue_counts.error ?? 0} errors, {sim.issue_counts.warning ?? 0} warnings.
      </p>
      <p className={subText}>
        Baseline: directory as of {dateInZone(data.snapshot.taken_at)}. From{" "}
        {data.scenario.source_filename ?? "manual edits"}.
      </p>
      <Lifecycle data={data} onGone={onGone} />
      <div className="border-border mt-4 flex gap-4 border-b" role="tablist">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={
              tab === key
                ? "text-fg border-fg -mb-px border-b-2 pb-2 text-sm font-medium"
                : "text-fg-muted hover:text-fg pb-2 text-sm"
            }
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "chart" ? <ScenarioChart data={data} /> : null}
      {tab === "changes" ? <ChangesTable data={data} /> : null}
      {tab === "issues" ? <IssueList data={data} /> : null}
      {tab === "managers" ? <ManagerTable data={data} /> : null}
      {tab === "left-out" ? (
        leftOut.length ? (
          <ImportTable rows={leftOut} picks={{}} onPick={() => undefined} />
        ) : (
          <p className={`${subText} mt-3`}>Every row of the file made it into the scenario.</p>
        )
      ) : null}
    </section>
  );
}

function ScenarioTitle({ id, name, status }: { id: string; name: string; status: string }) {
  const rename = useRenameOrgScenario();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  if (editing) {
    return (
      <form
        className="flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (value.trim()) {
            rename.mutate({ id, name: value.trim() }, { onSuccess: () => setEditing(false) });
          }
        }}
      >
        <Input value={value} onChange={(e) => setValue(e.target.value)} autoFocus />
        <Button size="sm" type="submit" disabled={rename.isPending}>
          Save
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </form>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <h2 className="text-fg text-base font-semibold">{name}</h2>
      <StatusBadge status={status} />
      <button
        className="text-fg-muted hover:text-fg text-xs underline"
        onClick={() => {
          setValue(name);
          setEditing(true);
        }}
      >
        Rename
      </button>
    </div>
  );
}

/**
 * The steps from an import to a directory that shows it, with only the
 * buttons that make sense in the current state. The server enforces every
 * transition; this only avoids offering a button that would be refused.
 */
function Lifecycle({ data, onGone }: { data: ScenarioDetail; onGone: () => void }) {
  const act = useOrgScenarioAction();
  const sc = data.scenario;
  const id = sc.id;
  const errors = data.simulation.issue_counts.error ?? 0;
  const count = (st: string) => data.changes.filter((c) => c.ad_state === st).length;
  const verified = data.changes.filter((c) => c.verified_at).length;
  const total = data.changes.length;
  const write = data.write;

  const run = (action: Parameters<typeof act.mutate>[0]["action"], confirmText?: string) => {
    if (confirmText && !window.confirm(confirmText)) {
      return;
    }
    act.mutate({ id, action }, { onSuccess: () => action === "delete" && onGone() });
  };
  const btn = (
    label: string,
    action: Parameters<typeof act.mutate>[0]["action"],
    opts: {
      variant?: "solid" | "outline" | "ghost";
      tone?: "danger";
      disabled?: boolean;
      confirm?: string;
    } = {},
  ) => (
    <Button
      key={action}
      size="sm"
      variant={opts.variant ?? "outline"}
      tone={opts.tone}
      disabled={act.isPending || opts.disabled}
      onClick={() => run(action, opts.confirm)}
    >
      {label}
    </Button>
  );

  let text: ReactNode = null;
  const buttons: ReactNode[] = [];
  const del = btn("Delete", "delete", {
    variant: "ghost",
    tone: "danger",
    confirm: `Delete "${sc.name}" for good? The log keeps what it contained.`,
  });
  const neverWritten = !data.changes.some((c) => c.ad_state === "written");

  switch (sc.status) {
    case "draft":
      text =
        errors > 0
          ? `Draft. ${errors} error(s) in the findings must be fixed before it can be approved.`
          : "Draft. Edit the changes, then approve to freeze it for AD.";
      buttons.push(
        btn("Approve", "approve", { variant: "solid", disabled: errors > 0 || total === 0 }),
      );
      buttons.push(btn("Archive", "archive"), del);
      break;
    case "approved":
      text = sc.tested_at
        ? `Approved by ${sc.approved_by_upn}. Tested against AD ${dateInZone(sc.tested_at)}: ${count("would")} would be written, ${count("already")} already set, ${count("drift")} changed in AD since the baseline, ${count("refused") + count("failed")} refused or failed.`
        : `Approved by ${sc.approved_by_upn}. Next: test it against AD. The test reads every account and writes nothing.`;
      buttons.push(
        btn("Test against AD", "test", {
          variant: sc.tested_at ? "outline" : "solid",
          disabled: !write.available,
        }),
      );
      buttons.push(
        btn("Apply to AD", "apply", {
          variant: "solid",
          disabled: !write.available || !sc.tested_at || count("would") === 0,
          confirm: `Write ${count("would")} change(s) to on-prem AD now? Changes that drifted or were refused are skipped.`,
        }),
      );
      buttons.push(btn("Reopen for editing", "reopen"), btn("Archive", "archive"), del);
      break;
    case "testing":
      text = "Waiting for the worker to read AD (within 5 minutes). Nothing is written.";
      break;
    case "applying":
      text = `Writing to AD: ${count("written") + count("already")} of ${total} done in AD, ${verified} visible in Entra. Entra shows a change after the next directory sync (up to about 30 minutes).`;
      break;
    case "partial":
      text = `Partly applied: ${verified} of ${total} changes are in the directory. ${count("failed")} failed, ${count("drift")} drifted, ${count("refused")} refused — see the Changes tab.`;
      buttons.push(
        btn("Retry failed", "retry", {
          variant: "solid",
          disabled: !write.available || count("failed") === 0,
        }),
      );
      buttons.push(btn("Reopen for editing", "reopen"), btn("Archive", "archive"));
      if (neverWritten) {
        buttons.push(del);
      }
      break;
    case "applied":
      text = `Applied: all ${total} changes are in AD and visible in Entra${sc.applied_at ? ` (${dateInZone(sc.applied_at)})` : ""}.`;
      buttons.push(btn("Archive", "archive"));
      break;
    case "archived":
      text = "Archived.";
      buttons.push(btn("Restore", "restore"));
      if (neverWritten) {
        buttons.push(del);
      }
      break;
  }

  return (
    <div className="border-border bg-bg-subtle mt-3 rounded border p-3">
      <p className="text-fg text-sm">{text}</p>
      {!write.available && ["approved", "partial"].includes(sc.status) ? (
        <p className={`${subText} mt-1`}>{write.reason}</p>
      ) : null}
      {buttons.length ? <div className="mt-2 flex flex-wrap gap-2">{buttons}</div> : null}
      {act.error ? <p className="text-danger-fg mt-2 text-sm">{errorMessage(act.error)}</p> : null}
    </div>
  );
}

function worstIssues(issues: SimIssue[]) {
  const rank = { error: 3, warning: 2, info: 1 } as const;
  const out = new Map<string, "error" | "warning" | "info">();
  for (const i of issues) {
    if (!i.subject_id) {
      continue;
    }
    const cur = out.get(i.subject_id);
    if (!cur || rank[i.severity] > rank[cur]) {
      out.set(i.subject_id, i.severity);
    }
  }
  return out;
}

function ScenarioChart({ data }: { data: ScenarioDetail }) {
  // The scenario brings its baseline for the people around its changes; the
  // snapshot fills in everybody else, so a hand-built scenario can start from
  // any part of the org. Scenario rows win where both have the person.
  const everyone = useOrgSnapshot(true).data?.people;
  const before = useMemo(
    () => toMap([...(everyone ?? []), ...data.people]),
    [everyone, data.people],
  );
  const after = useMemo(() => project(before, data.changes), [before, data.changes]);
  const changes = useMemo(() => indexChanges(data.changes), [data.changes]);
  const issues = useMemo(() => worstIssues(data.simulation.issues), [data.simulation.issues]);
  const [mode, setMode] = useState<ChartMode>("after");
  const [root, setRoot] = useState<string | null>(null);
  const [showAccounts, setShowAccounts] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [presenting, setPresenting] = useState(false);

  const map = mode === "after" ? after : before;
  const changed = [...changes.keys()];
  const top = useMemo(() => topOf(map), [map]);
  const defaultRoot = changed.length ? commonAncestor(map, changed) : top;
  const rootId = root && map.has(root) ? root : defaultRoot;

  // Expanded: everyone on a line from a changed person to the top.
  const open = useMemo(() => {
    const s = new Set<string>();
    for (const id of changed) {
      for (const x of pathToTop(map, id).slice(1)) {
        s.add(x);
      }
    }
    for (const id of changed) {
      s.add(id);
    }
    return s;
  }, [map, changed]);
  const initiallyOpen = useCallback(
    (id: string, depth: number) => open.has(id) || (changed.length === 0 && depth < 1),
    [open, changed.length],
  );

  if (!rootId) {
    return <Notice>Nothing to draw.</Notice>;
  }
  const parent = map.get(rootId)?.manager_id;
  return (
    <>
      <ChartControls>
        <div className="flex items-center gap-1" role="group" aria-label="Show">
          {(["before", "after"] as const).map((m) => (
            <Button
              key={m}
              size="sm"
              variant={mode === m ? "solid" : "outline"}
              onClick={() => {
                setMode(m);
                setRoot(null);
              }}
            >
              {m === "before" ? "Today" : "After the change"}
            </Button>
          ))}
        </div>
        <PersonSearch
          people={[...map.values()]}
          label=""
          value={null}
          onChange={(p) => p && setRoot(p.id)}
        />
        <Button
          size="sm"
          variant="outline"
          disabled={!parent || parent === rootId || !map.has(parent)}
          onClick={() => parent && setRoot(parent)}
        >
          Up one level
        </Button>
        <label className="text-fg-muted flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={showAccounts}
            onChange={(e) => setShowAccounts(e.target.checked)}
          />
          Show mailboxes and service accounts
        </label>
        <Button size="sm" variant="outline" onClick={() => setPresenting(true)}>
          Presentation / PDF
        </Button>
        <Legend />
      </ChartControls>
      {presenting ? (
        <PresentationDialog
          map={map}
          rootId={rootId}
          movedIds={
            mode === "after"
              ? new Set(data.changes.filter((c) => c.field === "manager").map((c) => c.subject_id))
              : new Set()
          }
          scopeIds={
            new Set([
              ...data.changes.map((c) => c.subject_id),
              ...(data.import_report?.rows ?? []).flatMap((r) =>
                r.person.id ? [r.person.id] : [],
              ),
            ])
          }
          defaultTitle={slideTitle(map.get(rootId))}
          defaultSubtitle={
            mode === "after"
              ? `Target structure from "${data.scenario.name}", {n} people`
              : "As in the directory today, {n} people"
          }
          source={`HROps · ${data.scenario.source_filename ?? "built by hand"} · directory as of ${dateInZone(data.snapshot.taken_at)}`}
          onClose={() => setPresenting(false)}
        />
      ) : null}
      <OrgChart
        key={`${mode}-${rootId}`}
        before={before}
        after={after}
        mode={mode}
        rootId={rootId}
        changes={changes}
        issueLevel={issues}
        showAccounts={showAccounts}
        initiallyOpen={initiallyOpen}
        selected={selected}
        onSelect={setSelected}
      />
      {selected ? (
        <PersonDetail
          person={map.get(selected)}
          before={before}
          after={after}
          data={data}
          people={[...before.values()]}
          onRoot={() => setRoot(selected)}
        />
      ) : (
        <p className={`${subText} mt-2`}>
          Click a box for details
          {data.scenario.status === "draft" ? " and to change that person" : ""}. Use Draw from here
          and Up one level to move around the org.
        </p>
      )}
    </>
  );
}

/** "IT organisation" when the root has a department, else "Carl Wood's organisation". */
function slideTitle(root: OrgPerson | undefined): string {
  if (!root) {
    return "Organisation";
  }
  const dept = (root.department ?? "").replace(/^\d+\s+/, "").trim();
  return dept ? `Norican ${dept} organisation` : `${personName(root)}'s organisation`;
}

function ChartControls({ children }: { children: ReactNode }) {
  return <div className="mt-3 flex flex-wrap items-center gap-3">{children}</div>;
}

function Legend() {
  const item = (cls: string, label: string) => (
    <span className="flex items-center gap-1">
      <span className={`inline-block h-3 w-4 rounded-sm border-2 ${cls}`} />
      {label}
    </span>
  );
  return (
    <span className="text-fg-muted ml-auto flex flex-wrap items-center gap-3 text-xs">
      {item("border-accent bg-accent-subtle", "new manager")}
      {item("border-warning bg-accent-subtle", "title or department")}
      {item("border-danger bg-bg-subtle", "error")}
      <span className="flex items-center gap-1">
        <span className="border-border-strong inline-block h-3 w-4 rounded-sm border-2 border-dashed" />
        disabled
      </span>
    </span>
  );
}

function PersonDetail({
  person,
  before,
  after,
  data,
  people,
  onRoot,
}: {
  person: OrgPerson | undefined;
  before: OrgMap;
  after: OrgMap;
  data: ScenarioDetail;
  people: OrgPerson[];
  onRoot: () => void;
}) {
  if (!person) {
    return null;
  }
  const mine = data.changes.filter((c) => c.subject_id === person.id);
  const issues = data.simulation.issues.filter(
    (i) => i.subject_id === person.id || (i.related_ids ?? []).includes(person.id),
  );
  const name = (m: OrgMap, id: string | null | undefined) => (id && m.get(id)?.display_name) || "—";
  return (
    <div className="border-border bg-bg-subtle mt-3 rounded border p-3 text-sm">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <span className="font-semibold">{person.display_name}</span>{" "}
          <span className={subText}>{person.upn}</span>
        </div>
        <Button size="sm" variant="outline" onClick={onRoot}>
          Draw from here
        </Button>
      </div>
      <div className={subText}>
        {[person.job_title, person.department, person.company_name, person.country]
          .filter(Boolean)
          .join(" · ")}
      </div>
      {mine.map((c) => (
        <div key={c.field} className="mt-1">
          <span className={subText}>{FIELD_LABEL[c.field]}: </span>
          {c.field === "manager" ? (
            <>
              {name(before, c.old)} → <strong>{name(after, c.new)}</strong>
            </>
          ) : (
            <>
              {c.old ?? "—"} → <strong>{c.new}</strong>
            </>
          )}
        </div>
      ))}
      {issues.map((i) => (
        <div key={i.code + i.detail} className="mt-1 flex items-start gap-2">
          <Badge tone={SEVERITY_TONE[i.severity]} variant="subtle">
            {i.severity}
          </Badge>
          <span>{i.detail}</span>
        </div>
      ))}
      {data.scenario.status === "draft" ? (
        <ChangeEditor scenarioId={data.scenario.id} people={people} fixed={person} />
      ) : null}
    </div>
  );
}

const AD_STATE: Record<string, { label: string; tone: BadgeProps["tone"] }> = {
  pending: { label: "not yet", tone: "neutral" },
  would: { label: "would write", tone: "info" },
  written: { label: "written", tone: "success" },
  already: { label: "already set", tone: "success" },
  drift: { label: "changed in AD", tone: "warning" },
  refused: { label: "refused", tone: "danger" },
  failed: { label: "failed", tone: "danger" },
};

function ChangesTable({ data }: { data: ScenarioDetail }) {
  const before = toMap(data.people);
  const remove = useDeleteOrgChange();
  const editable = data.scenario.status === "draft";
  const notes = new Map(
    (data.import_report?.rows ?? []).map((r) => [r.key, r.notes] as [string, string[]]),
  );
  const rows = [...data.changes].sort(
    (a, b) =>
      (before.get(a.subject_id)?.display_name ?? "").localeCompare(
        before.get(b.subject_id)?.display_name ?? "",
      ) || a.field.localeCompare(b.field),
  );
  const show = (c: OrgChange, v: string | null | undefined) =>
    c.field === "manager" ? (v && before.get(v)?.display_name) || v || "—" : (v ?? "—");
  return (
    <div className="mt-3">
      {editable ? <AddChange scenarioId={data.scenario.id} /> : null}
      <div className="mt-3 overflow-x-auto">
        <table className={table}>
          <thead>
            <tr>
              <th className={th}>Person</th>
              <th className={th}>Field</th>
              <th className={th}>Today → new</th>
              <th className={th}>AD / Entra</th>
              <th className={th}>Notes</th>
              {editable ? <th className={th} /> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const p = before.get(c.subject_id);
              const st = AD_STATE[c.ad_state ?? "pending"];
              return (
                <tr key={c.id}>
                  <td className={td}>
                    {p?.display_name ?? c.subject_upn}
                    <div className={subText}>{c.subject_upn}</div>
                  </td>
                  <td className={td}>
                    {FIELD_LABEL[c.field] ?? c.field}
                    {c.source === "manual" ? <div className={subText}>added by hand</div> : null}
                  </td>
                  <td className={td}>
                    <div className="line-through opacity-60">{show(c, c.old)}</div>
                    <div className="font-medium">{show(c, c.new)}</div>
                  </td>
                  <td className={td}>
                    <Badge tone={st?.tone} variant="subtle">
                      {st?.label ?? c.ad_state}
                    </Badge>
                    {c.verified_at ? <div className="text-success-fg text-xs">in Entra</div> : null}
                    {c.ad_detail ? <div className={subText}>{c.ad_detail}</div> : null}
                  </td>
                  <td className={`${td} text-warning-fg max-w-72 text-xs`}>
                    {(notes.get(c.source_row ?? "") ?? []).map((n) => (
                      <div key={n}>{n}</div>
                    ))}
                  </td>
                  {editable ? (
                    <td className={td}>
                      <Button
                        size="sm"
                        variant="ghost"
                        tone="danger"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate({ id: data.scenario.id, changeId: c.id })}
                      >
                        Remove
                      </Button>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {remove.error ? <Notice tone="danger">{errorMessage(remove.error)}</Notice> : null}
    </div>
  );
}

/** Pick a person from the snapshot, a field, a value. Draft only. */
function AddChange({ scenarioId }: { scenarioId: string }) {
  const people = useOrgSnapshot(true).data?.people ?? [];
  return (
    <div className="border-border rounded border p-3">
      <div className="text-fg text-sm font-medium">Add or change a value by hand</div>
      <ChangeEditor scenarioId={scenarioId} people={people} />
    </div>
  );
}

/**
 * One person, one field, one new value. With `fixed` the person is given
 * (the box clicked in the chart); without it, the person is searched for.
 */
function ChangeEditor({
  scenarioId,
  people,
  fixed,
}: {
  scenarioId: string;
  people: OrgPerson[];
  fixed?: OrgPerson;
}) {
  const put = usePutOrgChange();
  const [picked, setWho] = useState<OrgPerson | null>(null);
  const who = fixed ?? picked;
  const [field, setField] = useState<
    | "manager"
    | "job_title"
    | "department"
    | "company_name"
    | "office_location"
    | "business_category"
  >("manager");
  const [value, setValue] = useState("");
  const [manager, setManager] = useState<OrgPerson | null>(null);
  const ready = who && (field === "manager" ? manager : value.trim());
  return (
    <div>
      <div className="mt-2 flex flex-wrap items-end gap-3">
        {fixed ? null : (
          <PersonSearch people={people} label="Person" value={who} onChange={setWho} />
        )}
        <label className="text-fg-muted flex flex-col text-xs">
          Field
          <select
            className={control}
            value={field}
            onChange={(e) => setField(e.target.value as typeof field)}
          >
            <option value="manager">Manager</option>
            <option value="job_title">Job title</option>
            <option value="department">Department</option>
            <option value="company_name">Company</option>
            <option value="office_location">Office (L2 Area)</option>
            <option value="business_category">Business category (L2 Area)</option>
          </select>
        </label>
        {field === "manager" ? (
          <PersonSearch people={people} label="New manager" value={manager} onChange={setManager} />
        ) : (
          <label className="text-fg-muted flex flex-col text-xs">
            New value
            <Input value={value} onChange={(e) => setValue(e.target.value)} />
          </label>
        )}
        <Button
          size="sm"
          disabled={!ready || put.isPending}
          onClick={() =>
            who &&
            put.mutate(
              {
                id: scenarioId,
                body: {
                  subject_id: who.id,
                  field,
                  new: field === "manager" ? (manager?.id ?? "") : value.trim(),
                },
              },
              {
                onSuccess: () => {
                  setValue("");
                  setManager(null);
                },
              },
            )
          }
        >
          Add
        </Button>
      </div>
      {who ? (
        <p className={`${subText} mt-1`}>
          {fixed ? "In this scenario" : "Today"}: {who.job_title ?? "no title"} ·{" "}
          {who.department ?? "no department"} · manager{" "}
          {people.find((p) => p.id === who.manager_id)?.display_name ?? "none"}
        </p>
      ) : null}
      {put.error ? <p className="text-danger-fg mt-1 text-sm">{errorMessage(put.error)}</p> : null}
    </div>
  );
}

function IssueList({ data }: { data: ScenarioDetail }) {
  const order = ["error", "warning", "info"] as const;
  const issues = [...data.simulation.issues].sort(
    (a, b) => order.indexOf(a.severity) - order.indexOf(b.severity),
  );
  if (issues.length === 0) {
    return <p className={`${subText} mt-3`}>No findings.</p>;
  }
  return (
    <ul className="mt-3 space-y-2 text-sm">
      {issues.map((i) => (
        <li key={i.code + i.detail} className="flex items-start gap-2">
          <Badge tone={SEVERITY_TONE[i.severity]} variant="subtle">
            {i.severity}
          </Badge>
          <span>{i.detail}</span>
        </li>
      ))}
    </ul>
  );
}

function ManagerTable({ data }: { data: ScenarioDetail }) {
  const before = toMap(data.people);
  const names = (ids: string[]) =>
    ids.map((id) => before.get(id)?.display_name ?? id).join(", ") || "—";
  return (
    <div className="mt-3 overflow-x-auto">
      <table className={table}>
        <thead>
          <tr>
            <th className={th}>Manager</th>
            <th className={th}>Direct reports</th>
            <th className={th}>Gains</th>
            <th className={th}>Loses</th>
            <th className={th}>Keeps, not in the file</th>
          </tr>
        </thead>
        <tbody>
          {data.simulation.managers.map((m) => (
            <tr key={m.id}>
              <td className={td}>{before.get(m.id)?.display_name ?? m.id}</td>
              <td className={td}>
                {m.before} → <strong>{m.after}</strong>
              </td>
              <td className={`${td} text-xs`}>{names(m.gained)}</td>
              <td className={`${td} text-xs`}>{names(m.lost)}</td>
              <td className={`${td} text-xs`}>{names(m.not_in_file)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className={`${subText} mt-2`}>
        Counts are people only; mailboxes and service accounts are left out.
      </p>
    </div>
  );
}

// ─── directory, no scenario ──────────────────────────────────────────────

/**
 * `initialRoot` is `?root=<id>`, which the Active Directory view's "Show in
 * chart" sets: the chart opens on that person, selected. An id the chart's
 * set does not hold (a disabled account nobody reports to) falls back to the
 * top rather than drawing nothing.
 */
function DirectoryView({
  people,
  initialRoot,
}: {
  people: OrgPerson[];
  initialRoot?: string | null;
}) {
  const map = useMemo(() => toMap(people), [people]);
  const empty = useMemo(() => indexChanges([]), []);
  const noIssues = useMemo(() => new Map<string, "error" | "warning" | "info">(), []);
  const top = useMemo(() => topOf(map) ?? people[0]?.id, [map, people]);
  const [root, setRoot] = useState<string | null>(initialRoot ?? null);
  const [query, setQuery] = useState("");
  const [showAccounts, setShowAccounts] = useState(false);
  const [selected, setSelected] = useState<string | null>(initialRoot ?? null);
  const [presenting, setPresenting] = useState(false);
  const initiallyOpen = useCallback((_: string, depth: number) => depth < 1, []);
  const rootId = root && map.has(root) ? root : top;

  const q = query.trim().toLowerCase();
  const hits =
    q.length < 2
      ? []
      : people
          .filter(
            (p) =>
              p.account_enabled &&
              ((p.display_name ?? "").toLowerCase().includes(q) ||
                (p.upn ?? "").toLowerCase().includes(q)),
          )
          .slice(0, 8);

  if (!rootId) {
    return <Notice>The snapshot is empty.</Notice>;
  }
  const parent = map.get(rootId)?.manager_id;
  const sel = selected ? map.get(selected) : undefined;
  return (
    <section className="border-border mt-6 border-t pt-4">
      <h2 className="text-fg text-sm font-semibold">Directory today</h2>
      <ChartControls>
        <div className="relative">
          <Input
            placeholder="Find a person to draw from…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {hits.length ? (
            <ul className="border-border bg-bg absolute z-10 mt-1 w-96 rounded border shadow">
              {hits.map((p) => (
                <li key={p.id}>
                  <button
                    className="hover:bg-bg-subtle w-full px-3 py-1 text-left text-sm"
                    onClick={() => {
                      setRoot(p.id);
                      setQuery("");
                    }}
                  >
                    {p.display_name}
                    <span className={`${subText} ml-2`}>{p.job_title}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={!parent || parent === rootId || !map.has(parent)}
          onClick={() => parent && setRoot(parent)}
        >
          Up one level
        </Button>
        <label className="text-fg-muted flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={showAccounts}
            onChange={(e) => setShowAccounts(e.target.checked)}
          />
          Show mailboxes and service accounts
        </label>
        <Button size="sm" variant="outline" onClick={() => setPresenting(true)}>
          Presentation / PDF
        </Button>
      </ChartControls>
      {presenting ? (
        <PresentationDialog
          map={map}
          rootId={rootId}
          movedIds={new Set()}
          defaultTitle={slideTitle(map.get(rootId))}
          defaultSubtitle="As in the directory today, {n} people"
          source="HROps · directory snapshot"
          onClose={() => setPresenting(false)}
        />
      ) : null}
      <OrgChart
        key={rootId}
        before={map}
        after={map}
        mode="before"
        rootId={rootId}
        changes={empty}
        issueLevel={noIssues}
        showAccounts={showAccounts}
        initiallyOpen={initiallyOpen}
        selected={selected}
        onSelect={setSelected}
      />
      {sel ? (
        <div className="border-border bg-bg-subtle mt-3 flex items-baseline justify-between gap-3 rounded border p-3 text-sm">
          <div>
            <span className="font-semibold">{sel.display_name}</span>{" "}
            <span className={subText}>
              {[sel.upn, sel.job_title, sel.department, sel.company_name, sel.country]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </div>
          <Button size="sm" variant="outline" onClick={() => setRoot(sel.id)}>
            Draw from here
          </Button>
        </div>
      ) : null}
    </section>
  );
}
